import Phaser from "phaser";
import {
	type Entity,
	type EntityId,
	FUEL_STATION_RADIUS,
	FUEL_STATION_RANGE,
	findEntity,
	HEAT_WARNING,
	type PlayerIndex,
	type Ship,
	type ShipColor,
	type SimEvent,
	type WorldState,
} from "../../sim";
import { wrappedAxisDelta } from "../../sim/math/torus";
import { ANIMATIONS, type AnimationKey, type ImageKey } from "../assets";
import { prefersReducedMotion } from "../motion";
import {
	easeTowards,
	type FreeCamera,
	LOOK_AHEAD_EASE_MS,
	LOOK_AHEAD_EASE_REDUCED_MS,
	lookAheadTarget,
	SPEED_ZOOM_PER_SPEED,
	type SpeedZoom,
} from "./camera";
import type { CorrectionSmoother } from "./correction";
import type { Interpolator, Pose } from "./interpolation";
import { cameraZoom, type Rect } from "./layout";
import { drawObjective } from "./objective-art";
import { ParticleFx } from "./particle-fx";
import { FUEL_STATION_COLOR, POWER_UP_ART } from "./power-up-art";

const DEPTH: Record<Entity["kind"] | "background" | "effect", number> = {
	background: 0,
	planet: 10,
	spawnPoint: 20,
	pickup: 25,
	asteroid: 30,
	missile: 40,
	laser: 45,
	ship: 50,
	effect: 60,
};

const HIT_FLASH_MS = 90;
/** In-world laser heat bar under ships (world units). */
const HEAT_BAR = { width: 56, height: 5, gap: 8 };
const HEAT_COLORS = { cool: 0x62e0ff, hot: 0xf4c542, overheated: 0xff4d5e };
/** Shield bubble: gap around the hull (world units), colour, and flash length on a hit. */
/** Dots in a fueling station's dotted range circle. */
const FUEL_STATION_DOTS = 32;

const SHIELD = { gap: 10, color: 0x58d68d, flashMs: 160 };
/** Multiply tint of a frozen ship. */
const FROZEN_TINT = 0x9fe8ff;
/** Multiply tint of a homing missile while it is still guided. */
const HOMING_TINT = 0xff6070;
/** Free replay camera (I-014): ships are drawn at least this many pixels wide. */
const FREE_CAMERA_MIN_SHIP_PX = 16;
/** Exhaust particles per side per frame while warp speed is active. */
const WARP_EXHAUST = 3;
/** Upper bound on idle pooled sprites per texture (a burst of lasers is ~80). */
const MAX_POOLED_PER_TEXTURE = 128;

/** legacy background: the 1024 px starfield tiled at ~400 world units (WorldBackgroundSpriteManager). */
const BACKGROUND_TILE_UNITS = 400;

interface Effect {
	sprite: Phaser.GameObjects.Sprite;
	x: number;
	y: number;
}

/**
 * One player's view of the world: a Layer rendered only by this viewport's camera.
 * The camera never scrolls; instead every object is placed at its shortest wrapped offset
 * from the followed ship, which makes the torus seamless (replaces the legacy 3×3 copies).
 */
export class ViewportView {
	readonly layer: Phaser.GameObjects.Layer;
	private readonly background: Phaser.GameObjects.TileSprite;
	private readonly sprites = new Map<EntityId, Phaser.GameObjects.Sprite>();
	/** What each sprite was created for (online rollbacks can hand an id to another entity). */
	private readonly spriteIdentity = new Map<EntityId, string>();
	/** Destroyed this tick but not yet removed from the sim: hidden while the effect plays. */
	private readonly hidden = new Set<EntityId>();
	private readonly justInserted = new Set<EntityId>();
	private readonly pool = new Map<string, Phaser.GameObjects.Sprite[]>();
	private readonly effects: Effect[] = [];
	private readonly pose: Pose = { x: 0, y: 0, angle: 0 };
	private readonly center = { x: 0, y: 0 };
	private speed = 0;
	private readonly fx: ParticleFx;
	private followedShipId: EntityId | null = null;
	private readonly worldSize: { width: number; height: number };
	/**
	 * The camera centre in continuous (unwrapped) world coordinates: unlike `center` it never
	 * jumps by a world width at the seam, so particles emitted in it stay put on screen.
	 */
	private readonly cont = { x: 0, y: 0 };
	/**
	 * Camera framing: `zoomScale` < 1 shows more of the world; `offsetX` (fraction of the view
	 * width) shifts the followed ship off-centre. Used by the attract mode behind the menu.
	 */
	framing = { zoomScale: 1, offsetX: 0 };
	/** Replays (I-014): the viewer moves this camera instead of the view following a ship. */
	freeCamera: FreeCamera | null = null;
	/** Colour-blind friendly tags drawn under ships (player index → text), if enabled. */
	markers: Map<PlayerIndex, string> | null = null;
	private readonly markerTexts = new Map<PlayerIndex, Phaser.GameObjects.Text>();
	/** Per-frame overlays drawn on top of ships (heat bars, shields). */
	private readonly overlay: Phaser.GameObjects.Graphics;
	/** Ship id → time (ms) until which its shield shows a hit flash. */
	private readonly shieldFlash = new Map<EntityId, number>();
	/** Ships currently showing the white hull-damage flash (their tint is owned by it). */
	private readonly flashing = new Set<EntityId>();
	/** Camera x in view-local units (the followed ship is at 0). */
	private camX = 0;
	/** Camera y in view-local units (non-zero only with the look-ahead). */
	private camY = 0;
	/** Followed ship's velocity (look-ahead direction). */
	private readonly vel = { x: 0, y: 0 };
	/** Current (eased) look-ahead offset and its target, in world units. */
	private readonly look = { x: 0, y: 0 };
	private readonly lookTarget = { x: 0, y: 0 };
	/** Online only: fading offsets that smooth rollback corrections (P10.4). */
	corrections: CorrectionSmoother | null = null;
	/** Camera behaviour from the settings (P10.1); the 2008 camera is { false, "classic" }. */
	cameraStyle: { lookAhead: boolean; speedZoom: SpeedZoom } = {
		lookAhead: true,
		speedZoom: "classic",
	};

	constructor(
		private readonly scene: Phaser.Scene,
		readonly camera: Phaser.Cameras.Scene2D.Camera,
		readonly player: PlayerIndex,
		private rect: Rect,
		world: WorldState,
	) {
		this.layer = scene.add.layer();
		this.background = scene.make.tileSprite({ key: "background", width: 1, height: 1 }, false);
		this.background.setOrigin(0).setDepth(DEPTH.background);
		this.layer.add(this.background);
		this.setRect(rect);
		this.worldSize = { ...world.size };
		this.fx = new ParticleFx(scene, this.layer, DEPTH.ship - 1);
		this.overlay = scene.make.graphics({}, false).setDepth(DEPTH.effect);
		this.layer.add(this.overlay);

		// Start looking at the player's spawn point until their ship appears.
		const spawn = world.entities.filter((e) => e.kind === "spawnPoint")[player];
		if (spawn) Object.assign(this.center, spawn.pos);
		this.cont.x = this.center.x;
		this.cont.y = this.center.y;
	}

	setRect(rect: Rect): void {
		this.rect = rect;
		this.camera.setViewport(rect.x, rect.y, rect.width, rect.height);
	}

	/**
	 * After an online rollback: the world jumped to a corrected timeline. Drops sprites whose
	 * entity is gone or whose id now belongs to a different entity, and rebuilds "hidden"
	 * (destroyed, not yet removed) from the corrected world.
	 */
	resync(world: WorldState): void {
		const alive = new Map(world.entities.map((e) => [e.id, e]));
		for (const [id, sprite] of this.sprites) {
			const e = alive.get(id);
			if (e && this.spriteIdentity.get(id) === identityOf(e)) continue;
			this.release(sprite);
			this.sprites.delete(id);
			this.spriteIdentity.delete(id);
			this.flashing.delete(id);
			this.shieldFlash.delete(id);
		}
		this.hidden.clear();
		for (const id of world.removeQueue) this.hidden.add(id);
		this.justInserted.clear();
	}

	handleEvent(ev: SimEvent): void {
		switch (ev.type) {
			case "inserted":
				this.justInserted.add(ev.id);
				break;
			case "destroyed": {
				this.hidden.add(ev.id);
				const anim: AnimationKey | null =
					ev.kind === "ship" ? "shipDestroy" : ev.kind === "asteroid" ? "asteroidDestroy" : null;
				if (anim)
					this.spawnEffect(anim, ev.pos.x, ev.pos.y, ev.kind === "ship" ? 28 : 20, ev.angle);
				const at = this.toContinuous(ev.pos);
				this.fx.explosion(at.x, at.y, ev.kind === "ship");
				if (ev.kind === "ship" && this.ownShip(ev.id)) this.shake(260, 0.012);
				break;
			}
			case "damaged": {
				this.flash(ev.id);
				const at = this.toContinuous(ev.pos);
				this.fx.hit(at.x, at.y);
				if (ev.kind === "ship" && this.ownShip(ev.id)) this.shake(90, 0.004);
				break;
			}
			case "shieldHit":
				this.shieldFlash.set(ev.id, this.scene.time.now + SHIELD.flashMs);
				if (this.ownShip(ev.id)) this.shake(70, 0.003);
				break;
			case "removed": {
				const sprite = this.sprites.get(ev.id);
				if (sprite) this.release(sprite);
				this.sprites.delete(ev.id);
				this.spriteIdentity.delete(ev.id);
				this.hidden.delete(ev.id);
				this.justInserted.delete(ev.id);
				this.shieldFlash.delete(ev.id);
				this.flashing.delete(ev.id);
				break;
			}
			default:
				break;
		}
	}

	/**
	 * Returns a sprite to the pool (keyed by its current texture) instead of destroying it:
	 * lasers are created and removed up to 66 times a second per ship in every viewport.
	 */
	private release(sprite: Phaser.GameObjects.Sprite): void {
		sprite.stop();
		sprite.off(Phaser.Animations.Events.ANIMATION_COMPLETE);
		sprite.setTintMode(Phaser.TintModes.MULTIPLY).clearTint();
		sprite.setVisible(false).setActive(false);
		const key = sprite.texture.key;
		const list = this.pool.get(key) ?? [];
		if (list.length < MAX_POOLED_PER_TEXTURE) {
			list.push(sprite);
			this.pool.set(key, list);
		} else {
			sprite.destroy();
		}
	}

	/** Positions the pilot tags under visible ships (hidden when markers are off or no ship). */
	private renderMarkers(world: WorldState): void {
		if (!this.markers) return;
		for (const [player, label] of this.markers) {
			let text = this.markerTexts.get(player);
			if (!text) {
				text = this.scene.make.text(
					{
						text: label,
						style: {
							fontFamily: '"Orbitron Variable", system-ui, sans-serif',
							fontSize: "18px",
							fontStyle: "bold",
							color: "#ffffff",
							stroke: "#000000",
							strokeThickness: 4,
						},
					},
					false,
				);
				text.setOrigin(0.5, 0).setDepth(DEPTH.effect + 1);
				this.layer.add(text);
				this.markerTexts.set(player, text);
			}
			const shipId = world.players[player]?.shipId;
			const sprite = shipId == null ? undefined : this.sprites.get(shipId);
			const show = sprite?.visible === true && shipId != null && !this.hidden.has(shipId);
			text.setVisible(show);
			const below = world.rules.laserOverheat ? HEAT_BAR.gap + HEAT_BAR.height : 0;
			if (show && sprite) {
				text.setPosition(sprite.x, sprite.y + sprite.displayHeight / 2 + 4 + below);
			}
		}
	}

	/** Shield bubbles and laser heat bars (overheat rule) for every visible ship. */
	private renderOverlays(world: WorldState): void {
		const g = this.overlay;
		g.clear();
		// Objective modes (P11.2): the hill, the bases and the flags.
		const { width, height } = world.size;
		// Growing planets (P11.7): a glow that strengthens as a planet gains mass.
		if (world.rules.planetGrowth !== null) {
			for (const e of world.entities) {
				if (e.kind !== "planet" || e.mass <= e.baseMass) continue;
				const grown = Math.min(1, (e.mass - e.baseMass) / e.baseMass);
				const x = wrappedAxisDelta(this.center.x, e.pos.x, width);
				const y = wrappedAxisDelta(this.center.y, e.pos.y, height);
				g.lineStyle(4 + 10 * grown, 0xff9a3c, 0.15 + 0.45 * grown);
				g.strokeCircle(x, y, e.radius + 6 + 8 * grown);
			}
		}
		for (const s of world.fuelStations ?? []) {
			this.drawFuelStation(
				wrappedAxisDelta(this.center.x, s.pos.x, width),
				wrappedAxisDelta(this.center.y, s.pos.y, height),
			);
		}
		drawObjective(
			g,
			world,
			(x, y) => ({
				x: wrappedAxisDelta(this.center.x, x, width),
				y: wrappedAxisDelta(this.center.y, y, height),
			}),
			1,
			(player) => {
				const id = world.players[player]?.shipId;
				const sprite = id == null ? undefined : this.sprites.get(id);
				return sprite?.visible ? { x: sprite.x, y: sprite.y } : null;
			},
		);
		const blinkOff = !prefersReducedMotion() && world.tick % 26 < 13;
		const now = this.scene.time.now;
		for (const e of world.entities) {
			if (e.kind !== "ship") continue;
			const sprite = this.sprites.get(e.id);
			if (!sprite?.visible) continue;
			if (e.shield > 0 || (this.shieldFlash.get(e.id) ?? 0) > now) {
				this.drawShield(e, sprite, world.rules.shieldHits, (this.shieldFlash.get(e.id) ?? 0) > now);
			}
			if (!world.rules.laserOverheat || e.laserHeat <= 0) continue;
			const fill = Math.min(1, e.laserHeat / world.rules.laserHeatMax);
			const x = sprite.x - HEAT_BAR.width / 2;
			const y = sprite.y + e.radius + HEAT_BAR.gap;
			const color = e.laserOverheated
				? HEAT_COLORS.overheated
				: fill >= HEAT_WARNING
					? HEAT_COLORS.hot
					: HEAT_COLORS.cool;
			g.fillStyle(0x000000, 0.55).fillRect(x - 1, y - 1, HEAT_BAR.width + 2, HEAT_BAR.height + 2);
			if (e.laserOverheated && blinkOff) continue;
			g.fillStyle(color, 0.95).fillRect(x, y, HEAT_BAR.width * fill, HEAT_BAR.height);
		}
	}

	/**
	 * Fueling station (I-026): a dock with a fuel gauge, and its refuel area as a light dotted
	 * circle, so it doesn't read as a one-off fuel pod.
	 */
	private drawFuelStation(x: number, y: number): void {
		const g = this.overlay;
		const r = FUEL_STATION_RADIUS;
		const dash = Math.PI / FUEL_STATION_DOTS;
		g.lineStyle(2, FUEL_STATION_COLOR, 0.45);
		for (let i = 0; i < FUEL_STATION_DOTS; i++) {
			const start = 2 * i * dash;
			g.beginPath();
			g.arc(x, y, FUEL_STATION_RANGE, start, start + dash * 0.5);
			g.strokePath();
		}
		g.fillStyle(0x08262b, 0.95).fillCircle(x, y, r);
		g.lineStyle(3, FUEL_STATION_COLOR, 0.95).strokeCircle(x, y, r);
		g.fillStyle(FUEL_STATION_COLOR, 0.95).fillRect(x - 4, y - r / 2, 8, r);
	}

	/** A translucent bubble whose strength shows the hits left; bright while flashing. */
	private drawShield(
		ship: Ship,
		sprite: Phaser.GameObjects.Sprite,
		maxHits: number,
		flashing: boolean,
	): void {
		const strength = Math.min(1, ship.shield / Math.max(1, maxHits));
		const r = ship.radius + SHIELD.gap;
		const g = this.overlay;
		g.fillStyle(SHIELD.color, flashing ? 0.35 : 0.06 + 0.1 * strength);
		g.fillCircle(sprite.x, sprite.y, r);
		g.lineStyle(flashing ? 4 : 2, flashing ? 0xffffff : SHIELD.color, 0.4 + 0.5 * strength);
		g.strokeCircle(sprite.x, sprite.y, r);
	}

	/** Maps a world position to the continuous frame the particles live in. */
	private toContinuous(p: { x: number; y: number }): { x: number; y: number } {
		return {
			x: this.cont.x + wrappedAxisDelta(this.center.x, p.x, this.worldSize.width),
			y: this.cont.y + wrappedAxisDelta(this.center.y, p.y, this.worldSize.height),
		};
	}

	private ownShip(id: EntityId): boolean {
		return this.followedShipId === id;
	}

	/** Camera shake for hits on this viewport's own ship (skipped with reduced motion). */
	private shake(durationMs: number, intensity: number): void {
		if (prefersReducedMotion()) return;
		this.camera.shake(durationMs, intensity);
	}

	/** Hull-damage hit: the sprite flashes white for a moment. */
	private flash(id: EntityId): void {
		const sprite = this.sprites.get(id);
		if (!sprite) return;
		this.flashing.add(id);
		sprite.setTint(0xffffff).setTintMode(Phaser.TintModes.FILL);
		this.scene.time.delayedCall(HIT_FLASH_MS, () => {
			this.flashing.delete(id);
			if (sprite.active) sprite.setTintMode(Phaser.TintModes.MULTIPLY).clearTint();
		});
	}

	/** Frozen ships are tinted icy blue (unless a hit flash owns the tint right now). */
	private tintShip(sprite: Phaser.GameObjects.Sprite, ship: Ship): void {
		if (this.flashing.has(ship.id)) return;
		tint(sprite, ship.frozenTicks > 0 ? FROZEN_TINT : null);
	}

	render(world: WorldState, interp: Interpolator, alpha: number): void {
		const free = this.freeCamera;
		if (free) this.moveTo(free, world);
		else this.followShip(world, interp, alpha);

		const perSpeed = SPEED_ZOOM_PER_SPEED[this.cameraStyle.speedZoom];
		const zoom = free
			? free.zoom
			: cameraZoom(this.rect.height, this.speed, perSpeed) * this.framing.zoomScale;
		const viewW = this.rect.width / zoom;
		const viewH = this.rect.height / zoom;
		if (free) {
			this.look.x = 0;
			this.look.y = 0;
		} else this.updateLookAhead(viewW / 2, viewH / 2);
		this.camX = free ? 0 : this.framing.offsetX * viewW + this.look.x;
		this.camY = free ? 0 : this.look.y;
		this.camera.setZoom(zoom);
		this.camera.centerOn(this.camX, this.camY);

		this.renderBackground(world, viewW, viewH);
		this.fx.follow(this.cont.x, this.cont.y);

		const { width, height } = world.size;
		const halfW = viewW / 2;
		const halfH = viewH / 2;
		for (const e of world.entities) {
			const sprite = this.sprites.get(e.id) ?? this.createSprite(e);
			const pose = this.corrected(e.id, interp.sample(e, alpha, world.size, this.pose));
			const dx = wrappedAxisDelta(this.center.x, pose.x, width);
			const dy = wrappedAxisDelta(this.center.y, pose.y, height);
			const onScreen =
				Math.abs(dx - this.camX) < halfW + e.radius && Math.abs(dy - this.camY) < halfH + e.radius;
			sprite.setVisible(onScreen && !this.hidden.has(e.id));
			if (!sprite.visible) continue;
			sprite.setPosition(dx, dy).setRotation(pose.angle);
			this.updateTexture(sprite, e);
			if (e.kind === "ship") {
				this.tintShip(sprite, e);
				// Zoomed out over the whole map, ships would shrink to a pixel or two.
				const size = free ? Math.max(e.radius * 2, FREE_CAMERA_MIN_SHIP_PX / zoom) : e.radius * 2;
				if (sprite.displayWidth !== size) sprite.setDisplaySize(size, size);
			}
			if (e.kind === "missile") tint(sprite, e.homingTicks > 0 ? HOMING_TINT : null);
			if (e.kind === "ship" && e.thrusting) {
				const count = e.warpTicks > 0 ? WARP_EXHAUST : 1;
				this.fx.thrust(this.cont.x + dx, this.cont.y + dy, pose.angle, e.radius, count);
			}
		}
		this.renderOverlays(world);
		this.renderMarkers(world);

		for (const fx of this.effects) {
			fx.sprite.setPosition(
				wrappedAxisDelta(this.center.x, fx.x, width),
				wrappedAxisDelta(this.center.y, fx.y, height),
			);
		}
	}

	destroy(): void {
		this.layer.destroy();
	}

	/** Free camera: centre on its point, keeping the particles' continuous frame in step. */
	private moveTo(free: FreeCamera, world: WorldState): void {
		this.cont.x += wrappedAxisDelta(this.center.x, free.x, world.size.width);
		this.cont.y += wrappedAxisDelta(this.center.y, free.y, world.size.height);
		this.center.x = free.x;
		this.center.y = free.y;
	}

	private followShip(world: WorldState, interp: Interpolator, alpha: number): void {
		const shipId = world.players[this.player]?.shipId;
		const ship = shipId == null ? undefined : findEntity(world, shipId);
		// legacy: without a ship the viewport keeps its last centre and distance.
		if (ship?.kind !== "ship") return;
		if (this.followedShipId !== ship.id) {
			// A new ship (respawn): start centred instead of sliding over from the old one.
			this.look.x = 0;
			this.look.y = 0;
		}
		this.followedShipId = ship.id;
		const pose = this.corrected(ship.id, interp.sample(ship, alpha, world.size, this.pose));
		this.cont.x += wrappedAxisDelta(this.center.x, pose.x, world.size.width);
		this.cont.y += wrappedAxisDelta(this.center.y, pose.y, world.size.height);
		this.center.x = pose.x;
		this.center.y = pose.y;
		this.speed = Math.hypot(ship.vel.x, ship.vel.y);
		this.vel.x = ship.vel.x;
		this.vel.y = ship.vel.y;
	}

	/** Adds the fading online-rollback correction (P10.4) to a sampled pose. */
	private corrected(id: EntityId, pose: Pose): Pose {
		const off = this.corrections?.offset(id);
		if (off) {
			pose.x += off.x;
			pose.y += off.y;
			pose.angle += off.angle;
		}
		return pose;
	}

	/** Eases the look-ahead offset towards the followed ship's direction of travel. */
	private updateLookAhead(halfW: number, halfH: number): void {
		if (this.cameraStyle.lookAhead) lookAheadTarget(this.vel, halfW, halfH, this.lookTarget);
		else {
			this.lookTarget.x = 0;
			this.lookTarget.y = 0;
		}
		const ease = prefersReducedMotion() ? LOOK_AHEAD_EASE_REDUCED_MS : LOOK_AHEAD_EASE_MS;
		easeTowards(this.look, this.lookTarget, this.scene.game.loop.delta, ease);
	}

	/** World-anchored starfield: tiles divide the world evenly so the seam is invisible. */
	private renderBackground(world: WorldState, viewW: number, viewH: number): void {
		const texW = this.background.frame.width;
		const texH = this.background.frame.height;
		const tileW =
			world.size.width / Math.max(1, Math.floor(world.size.width / BACKGROUND_TILE_UNITS));
		const tileH =
			world.size.height / Math.max(1, Math.floor(world.size.height / BACKGROUND_TILE_UNITS));
		const sx = tileW / texW;
		const sy = tileH / texH;
		const left = this.center.x + this.camX - viewW / 2;
		const top = this.center.y + this.camY - viewH / 2;
		this.background
			.setPosition(this.camX - viewW / 2, this.camY - viewH / 2)
			.setSize(viewW + 2, viewH + 2)
			.setTileScale(sx, sy)
			.setTilePosition((left / sx) % texW, (top / sy) % texH);
	}

	private createSprite(e: Entity): Phaser.GameObjects.Sprite {
		const key = textureFor(e);
		const pooled = this.pool.get(key)?.pop();
		const sprite = pooled ?? this.scene.make.sprite({ key }, false);
		if (pooled) sprite.setActive(true).setRotation(0);
		else this.layer.add(sprite);
		sprite.setDisplaySize(e.radius * 2, e.radius * 2).setDepth(DEPTH[e.kind]);
		// A soft glow for lasers without touching the original art.
		sprite.setBlendMode(e.kind === "laser" ? Phaser.BlendModes.ADD : Phaser.BlendModes.NORMAL);
		this.sprites.set(e.id, sprite);
		this.spriteIdentity.set(e.id, identityOf(e));

		if (e.kind === "asteroid" && this.justInserted.has(e.id)) {
			sprite.play("asteroidInsert");
			sprite.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => sprite.setTexture("asteroid"));
		}
		this.justInserted.delete(e.id);
		return sprite;
	}

	private updateTexture(sprite: Phaser.GameObjects.Sprite, e: Entity): void {
		if (e.kind !== "ship" && e.kind !== "spawnPoint") return;
		const key = textureFor(e);
		if (sprite.texture.key !== key) {
			sprite.setTexture(key);
			sprite.setDisplaySize(e.radius * 2, e.radius * 2);
		}
	}

	private spawnEffect(
		anim: AnimationKey,
		x: number,
		y: number,
		radius: number,
		angle: number,
	): void {
		const sprite = this.scene.make.sprite({ key: ANIMATIONS[anim][0] }, false);
		sprite.setDepth(DEPTH.effect).setRotation(angle);
		sprite.play(anim);
		sprite.setDisplaySize(radius * 2, radius * 2);
		this.layer.add(sprite);
		const effect: Effect = { sprite, x, y };
		this.effects.push(effect);
		sprite.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
			this.effects.splice(this.effects.indexOf(effect), 1);
			sprite.destroy();
		});
	}
}

/** Sets or clears a multiply tint, touching the sprite only when it changes. */
function tint(sprite: Phaser.GameObjects.Sprite, color: number | null): void {
	if (color !== null && (!sprite.isTinted || sprite.tintTopLeft !== color)) sprite.setTint(color);
	else if (color === null && sprite.isTinted) sprite.clearTint();
}

const SHIP_TEXTURES: Record<ShipColor, { idle: ImageKey; thrust: ImageKey }> = {
	blue: { idle: "shipBlue", thrust: "shipBlueThrottle" },
	red: { idle: "shipRed", thrust: "shipRedThrottle" },
	green: { idle: "shipGreen", thrust: "shipGreenThrottle" },
	yellow: { idle: "shipYellow", thrust: "shipYellowThrottle" },
};

/** A sprite can be reused for an entity with the same identity (ships change texture). */
function identityOf(e: Entity): string {
	if (e.kind === "ship") return `ship:${e.color}`;
	if (e.kind === "spawnPoint") return "spawnPoint";
	if (e.kind === "asteroid") return `asteroid:${e.size}`;
	return textureFor(e);
}

function textureFor(e: Entity): ImageKey {
	switch (e.kind) {
		case "ship":
			return (
				e.thrusting ? SHIP_TEXTURES[e.color].thrust : SHIP_TEXTURES[e.color].idle
			) as ImageKey;
		case "planet":
			return e.planetType === "mars"
				? "planetMars"
				: e.planetType === "earth"
					? "planetEarth"
					: "planetPluto";
		case "asteroid":
			return "asteroid";
		case "laser":
			return "laser";
		case "missile":
			return "missile";
		case "spawnPoint":
			return e.occupied ? "spawnPointOccupied" : "spawnPointFree";
		case "pickup":
			return POWER_UP_ART[e.item].texture;
	}
}
